// Shared by SidebarNav (desktop) and MobileNav (below lg:).

import { buildSkedHref } from "@shared/leadgen/cta-rules";

export interface NavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
  /** Render as a non-interactive item with a "Soon" pill (M6/M7). */
  disabled?: boolean;
  /** Visually nested under the previous top-level item (eg. scroll anchors). */
  subItem?: boolean;
}

// Platforms / Geography / Landing Pages live as anchor sections inside
// /app/report for now; the standalone surfaces split out in a later
// release. Builder + Exports ship in M6 + M7 — disabled here so they
// don't 404, with a "soon" hint shown via the disabled flag.
//
// M13 — added subItems for the new dashboard sections (Channel mix /
// Depth / AI insights / Mobile / Ecommerce) so the sidebar mirrors the
// scroll order of /app/report. The Ecommerce section renders only when
// the property has ecommerce data; clicking that link on a non-ecommerce
// property is a silent no-op (no element with id="ecommerce" to scroll to).
//
// SKE-3431 — reordered to match the actual page scroll (AI insights is
// the first section, then Trend / Channel mix / Depth / Recommendations,
// then per-platform / per-device / per-page / per-channel-revenue /
// per-country sections). TimeSeries (#trend) and ExecutiveSummary
// (#actions) gained sidebar entries so the nav reads as a faithful
// table-of-contents rather than skipping sections silently.
export const navItems: NavItem[] = [
  { to: "/app/report", label: "Traffic report", icon: "fa-regular fa-grid-2", end: true },
  {
    to: "/app/report#insights",
    label: "AI insights",
    icon: "fa-regular fa-sparkles",
    subItem: true,
  },
  {
    to: "/app/report#trend",
    label: "Trend",
    icon: "fa-regular fa-chart-line",
    subItem: true,
  },
  {
    to: "/app/report#source-mix",
    label: "Channel mix",
    icon: "fa-regular fa-chart-pie",
    subItem: true,
  },
  {
    to: "/app/report#depth",
    label: "Depth",
    icon: "fa-regular fa-stairs",
    subItem: true,
  },
  {
    to: "/app/report#actions",
    label: "Recommendations",
    icon: "fa-regular fa-list-check",
    subItem: true,
  },
  {
    to: "/app/report#platforms",
    label: "Platforms",
    icon: "fa-regular fa-chart-column",
    subItem: true,
  },
  {
    to: "/app/report#mobile",
    label: "Mobile",
    icon: "fa-regular fa-mobile",
    subItem: true,
  },
  {
    to: "/app/report#landing-pages",
    label: "Landing pages",
    icon: "fa-regular fa-file-chart-column",
    subItem: true,
  },
  {
    to: "/app/report#ecommerce",
    label: "Ecommerce",
    icon: "fa-regular fa-cart-shopping",
    subItem: true,
  },
  {
    to: "/app/report#geography",
    label: "Geography",
    icon: "fa-regular fa-globe",
    subItem: true,
  },
  { to: "/app/utm-health", label: "Tag health", icon: "fa-regular fa-shield-check" },
  {
    to: "/app/utm-health#score",
    label: "Score",
    icon: "fa-regular fa-gauge-high",
    subItem: true,
  },
  {
    to: "/app/utm-health#issues",
    label: "Issues",
    icon: "fa-regular fa-triangle-exclamation",
    subItem: true,
  },
  {
    to: "/app/utm-health#flagged-urls",
    label: "Flagged URLs",
    icon: "fa-regular fa-list-ul",
    subItem: true,
  },
  {
    to: "/app/utm-health#validate",
    label: "Validate a link",
    icon: "fa-regular fa-flask-vial",
    subItem: true,
  },
  { to: "/app/builder", label: "UTM builder", icon: "fa-regular fa-link" },
  { to: "/app/taxonomy", label: "Taxonomy", icon: "fa-regular fa-book-open" },
  { to: "/app/exports", label: "Exports", icon: "fa-regular fa-arrow-down-to-line" },
  { to: "/app/settings", label: "Settings", icon: "fa-regular fa-gear" },
];

export const SKED_CROSS_SELL_HREF = buildSkedHref({
  medium: "app_sidebar",
  campaign: "cross_sell",
});
